
import { useSnackbar } from '@components/feedback/GlobalSnackbar';
import { queryKeys } from '@constants/queryKeys';
import { invalidateDropdown } from '@hooks/useDropdown';
import { authService } from '@services/auth.service';
import {
  masterService,
  type CreateCentreDto,
  type CreateMainCentreDto,
  type MasterResource,
  type UpdateMainCentreDto,
} from '@services/master.service';
import { settingsService, type UpdateSettingDto } from '@services/settings.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

const errMsg = (err: unknown): string =>
  (err as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ??
  'Something went wrong';

/* ── App Settings ─────────────────────────────────────────────────── */

export const useAppSettings = () =>
  useQuery({
    queryKey: queryKeys.appSettings.detail(),
    queryFn:  () => settingsService.get().then((r) => r.data.setting),
  });

export const useUpdateAppSettings = () => {
  const qc      = useQueryClient();
  const snackbar = useSnackbar();
  return useMutation({
    mutationFn: (body: UpdateSettingDto) =>
      settingsService.update(body).then((r) => r.data.setting),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.appSettings.detail(), data);
      snackbar.show('Settings saved', 'success');
    },
    onError: (err) => snackbar.show(errMsg(err), 'error'),
  });
};

/* ── Master data (banks / courses / centres) ──────────────────────── */

export const useMasterList = (resource: MasterResource) =>
  useQuery({
    queryKey: queryKeys.master.list(resource),
    queryFn:  () => masterService.list(resource).then((r) => r.data.items),
  });

export const useCreateMasterItem = (resource: MasterResource) => {
  const qc      = useQueryClient();
  const snackbar = useSnackbar();
  return useMutation({
    mutationFn: (name: string) =>
      masterService.create(resource, name).then((r) => r.data.item),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.master.list(resource) });
      if (resource === 'banks' || resource === 'courses') invalidateDropdown(resource);
      snackbar.show('Added successfully', 'success');
    },
    onError: (err) => snackbar.show(errMsg(err), 'error'),
  });
};

export const useCreateCentre = () => {
  const qc      = useQueryClient();
  const snackbar = useSnackbar();
  return useMutation({
    mutationFn: (body: CreateCentreDto) =>
      masterService.createCentre(body).then((r) => r.data.item),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.master.list('centres') });
      snackbar.show('Centre added successfully', 'success');
    },
    onError: (err) => snackbar.show(errMsg(err), 'error'),
  });
};

/* ── Main centres (the city above a centre; owns the GST identity) ── */

export const useMainCentreList = () =>
  useQuery({
    queryKey: queryKeys.master.list('main-centres'),
    queryFn:  () => masterService.listMainCentres().then((r) => r.data.items),
  });

/** Centres of one main centre — pass '' to skip the query until one is chosen. */
export const useCentreList = (mainCentreId: string) =>
  useQuery({
    queryKey: [...queryKeys.master.list('centres'), mainCentreId],
    queryFn:  () => masterService.listCentres(mainCentreId).then((r) => r.data.items),
    enabled:  !!mainCentreId,
  });

export const useCreateMainCentre = () => {
  const qc      = useQueryClient();
  const snackbar = useSnackbar();
  return useMutation({
    mutationFn: (body: CreateMainCentreDto) =>
      masterService.createMainCentre(body).then((r) => r.data.item),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.master.list('main-centres') });
      snackbar.show('Main centre added successfully', 'success');
    },
    onError: (err) => snackbar.show(errMsg(err), 'error'),
  });
};

/**
 * Edits a main centre — in practice its receipt header (name, address, contact
 * and tax registration). Every centre under it bills under these values.
 */
export const useUpdateMainCentre = (id: string) => {
  const qc      = useQueryClient();
  const snackbar = useSnackbar();
  return useMutation({
    mutationFn: (body: UpdateMainCentreDto) =>
      masterService.updateMainCentre(id, body).then((r) => r.data.item),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.master.list('main-centres') });
      snackbar.show('Institute details saved', 'success');
    },
    onError: (err) => snackbar.show(errMsg(err), 'error'),
  });
};

export const useDeleteMasterItem = (resource: MasterResource) => {
  const qc      = useQueryClient();
  const snackbar = useSnackbar();
  return useMutation({
    mutationFn: (id: string) =>
      masterService.remove(resource, id).then((r) => r.data.item),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.master.list(resource) });
      if (resource === 'banks' || resource === 'courses') invalidateDropdown(resource);
    },
    onError: (err) => snackbar.show(errMsg(err), 'error'),
  });
};

/* ── Profile / account (thin wrappers around authService) ─────────── */
/* Callers keep owning success/error handling via mutateAsync, matching */
/* the original page's per-action try/catch behavior exactly.          */

export const useUploadAvatar = () =>
  useMutation({ mutationFn: (file: File) => authService.uploadAvatar(file) });

export const usePatchMe = () =>
  useMutation({ mutationFn: (name: string) => authService.patchMe(name) });

export const useChangePassword = () =>
  useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      authService.changePassword(currentPassword, newPassword),
  });

export const useResendVerification = () =>
  useMutation({ mutationFn: () => authService.resendVerification() });

export const useVerifyEmail = () =>
  useMutation({ mutationFn: (code: string) => authService.verifyEmail(code) });
